import type { CountryCode } from "libphonenumber-js";

import { InternationalMdnInput, removeMdnFormatting } from "@expert/common-ui";
import { Stack, Text } from "@mantine/core";
import { useEffect, useMemo } from "react";

import { MdnInput } from "@/common-ui";
import { type Task, useCallbacksRemaining, useSession, useSessionActiveCallerId } from "@/sdk";
import { formatPhoneNumber, useAsyncLoading, validatePhoneNumber } from "@/shared-utils";

import type { CallbackFormVariant, CallbackSurface } from "../../types";

import { useCallbacksFormStore } from "../../callbacksForm.store";
import { useScheduleCallback } from "../../useScheduleCallback";
import { CallbackActions } from "./CallbackActions";
import { CallbackTypeSelection } from "./CallbackTypeSelection";

// TODO: remove onBackClick & afterSubmitClickDefault & reachedCallbackCutoff when DeprecatedSessionSummary is deleted.
interface ScheduleCallbackFormProps {
    callbackSurface: CallbackSurface;
    onBackClick?: () => void;
    afterSubmitClickDefault?: () => void;
    shouldShowActions?: boolean;
    reachedCallbackCutoff?: boolean;
    isInternationalPhoneEnabled?: boolean;
    defaultCountry?: CountryCode;
}

function getFormVariant(activeTask: Task | undefined) {
    if (!activeTask) {
        return "post-call";
    }

    return activeTask.status === "wrapping" ? "post-call" : "active-call";
}

export function ScheduleCallbackForm({
    callbackSurface,
    onBackClick,
    afterSubmitClickDefault,
    shouldShowActions = true,
    reachedCallbackCutoff,
    isInternationalPhoneEnabled,
    defaultCountry,
}: ScheduleCallbackFormProps) {
    const selectedCallbackMdn = useCallbacksFormStore((state) => state.selectedCallbackMdn);
    const callbackScheduled = useCallbacksFormStore((state) => state.callbackScheduled);
    const selectedCallbackOption = useCallbacksFormStore((state) => state.selectedCallbackOption);
    const { setCallbackType, setSelectedCallbackMdn } = useCallbacksFormStore.getState();

    const session = useSession();

    const activeTask = session.currentTask;
    const { scheduleCallback } = useScheduleCallback();
    const activeSessionMdn = useSessionActiveCallerId();

    const { callbacksRemaining } = useCallbacksRemaining();
    const defaultMdn = useMemo(
        () =>
            callbackScheduled && selectedCallbackMdn
                ? formatPhoneNumber(selectedCallbackMdn, defaultCountry, true).number || activeSessionMdn
                : activeSessionMdn,
        // oxlint-disable-next-line exhaustive-deps
        [activeSessionMdn],
    ); //? calculates once only when panel is mounted or activeSessionMdn changes

    const variant: CallbackFormVariant = getFormVariant(activeTask);
    const setDefaultValues = () => {
        switch (variant) {
            case "active-call":
                setCallbackType("callback-user-with-delay");
                break;
            case "post-call":
                setCallbackType("callback-user-now");
                useCallbacksFormStore.getState().setSelectedCallbackNowDelay(0);
                break;
        }
    };

    useEffect(() => {
        if (!callbackScheduled) {
            useCallbacksFormStore.getState().resetState();
            setDefaultValues();
            if (activeSessionMdn) setSelectedCallbackMdn(activeSessionMdn);
        } else if ((!selectedCallbackMdn || !validatePhoneNumber(selectedCallbackMdn).valid) && activeSessionMdn) {
            setSelectedCallbackMdn(activeSessionMdn);
        }
        // oxlint-disable-next-line exhaustive-deps
    }, [activeSessionMdn]); //? runs only when panel is mounted or activeSessionMdn changes

    const [isSubmitting, onSubmit] = useAsyncLoading(async () => {
        await scheduleCallback(callbackSurface);
        // running after submit actions if any were passed in via props
        if (afterSubmitClickDefault) afterSubmitClickDefault();
    });

    // Calculate phone validation
    const isCallbackMdnValid = selectedCallbackMdn
        ? validatePhoneNumber(selectedCallbackMdn, defaultCountry).valid
        : false;

    const props = {
        value: selectedCallbackMdn,
        onInput: (event: React.InputEvent<HTMLInputElement>) => {
            const normalizedMdn = removeMdnFormatting(event.currentTarget.value);
            setSelectedCallbackMdn(normalizedMdn);
        },
        autoFocus: true,
        maw: "30rem",
        mb: "xs",
        size: "sm",
        defaultCountry,
        defaultValue: defaultMdn,
    };

    return (
        <Stack>
            <Stack gap="xs" mb="xl">
                {callbacksRemaining <= 1 && (
                    <Text size="xs" c={"warning.5"}>
                        {callbacksRemaining} callback remaining
                    </Text>
                )}
                <Text size="xs">Callback number</Text>
                {isInternationalPhoneEnabled ? <InternationalMdnInput {...props} /> : <MdnInput {...props} />}
                <Text size="xs">Callback time</Text>
                <CallbackTypeSelection value={selectedCallbackOption} variant={variant} />
            </Stack>
            {shouldShowActions && (
                <CallbackActions
                    onSubmit={onSubmit}
                    onBackClick={onBackClick}
                    formVariant={variant}
                    isSubmitting={isSubmitting}
                    reachedCallbackCutoff={reachedCallbackCutoff}
                    isCallbackMdnValid={isCallbackMdnValid}
                    data-testid="call-controls-callback-schedule-complete"
                />
            )}
        </Stack>
    );
}
